import React from 'react';
import FormInput from '../../../components/ui/FormInput';
import { CalendarIcon, ClockIcon, PaperAirplaneIcon, MapPinIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import Select from 'react-select';

const TransferInfo = ({ transfers, isRoundTrip, onChange, onAdd, onRemove }) => {
  const handleTransferChange = (index, field, value) => {
    if (!transfers || !Array.isArray(transfers)) return;
    
    const updatedTransfers = transfers.map((transfer, i) => 
      i === index ? { ...transfer, [field]: value } : transfer
    );
    
    if (onChange) {
      // ReservationCreate için: onChange(updatedTransfers)
      // ReservationEdit için: onChange(index, field, value)
      // onChange fonksiyonunun parametrelerini kontrol et
      if (onChange.length === 1) {
        // ReservationCreate durumu
        onChange(updatedTransfers);
      } else {
        // ReservationEdit durumu
        onChange(index, field, value);
      }
    }
  };

  const handleSelectChange = (index, field, selectedOption) => {
    handleTransferChange(index, field, selectedOption ? selectedOption.value : '');
  };

  const getTransferTitle = (direction) => {
    return direction === 'arrival' ? 'Varış Transferi' : 'Dönüş Transferi';
  };

  // Custom styles for react-select
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#3B82F6' : '#D1D5DB',
      boxShadow: state.isFocused ? '0 0 0 2px #DBEAFE' : 'none',
      borderRadius: '0.5rem',
      padding: '2px',
      '&:hover': {
        borderColor: state.isFocused ? '#3B82F6' : '#9CA3AF',
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#3B82F6' : state.isFocused ? '#DBEAFE' : null,
      color: state.isSelected ? 'white' : '#111827',
    }),
  };

  // Para birimi seçenekleri
  const currencyOptions = [
    { value: 'EUR', label: 'EUR' },
    { value: 'USD', label: 'USD' },
    { value: 'TRY', label: 'TRY' },
    { value: 'GBP', label: 'GBP' }
  ];

  // transfers prop'unun güvenli kontrolü
  if (!transfers || !Array.isArray(transfers)) {
    return (
      <div className="space-y-8">
        <div className="text-center text-gray-500">
          Transfer bilgileri yükleniyor...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {transfers.map((transfer, index) => (
        <div key={index} className="bg-gray-50 p-6 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {getTransferTitle(transfer.direction)}
            </h3>
            {transfers.length > 1 && onRemove && (
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <FormInput
                id={`date-${index}`}
                name={`date-${index}`}
                label="Tarih"
                type="date"
                value={transfer.date}
                onChange={(e) => handleTransferChange(index, 'date', e.target.value)}
                required
                error={!transfer.date ? 'Tarih zorunludur' : null}
                icon={<CalendarIcon className="h-5 w-5 text-gray-400" />}
              />
            </div>

            <div className="sm:col-span-3">
              <FormInput
                id={`time-${index}`}
                name={`time-${index}`}
                label="Saat"
                type="time"
                value={transfer.time}
                onChange={(e) => handleTransferChange(index, 'time', e.target.value)}
                required
                error={!transfer.time ? 'Saat zorunludur' : null}
                icon={<ClockIcon className="h-5 w-5 text-gray-400" />}
              />
            </div>

            <div className="sm:col-span-3">
              <FormInput
                id={`flightNumber-${index}`}
                name={`flightNumber-${index}`}
                label="Uçuş Numarası"
                value={transfer.flightNumber}
                onChange={(e) => handleTransferChange(index, 'flightNumber', e.target.value)}
                placeholder="Örn: TK1234"
                icon={<PaperAirplaneIcon className="h-5 w-5 text-gray-400" />}
              />
            </div>

            <div className="sm:col-span-3">
              <FormInput
                id={`roomNumber-${index}`}
                name={`roomNumber-${index}`}
                label="Oda Numarası"
                value={transfer.roomNumber || ''}
                onChange={(e) => handleTransferChange(index, 'roomNumber', e.target.value)}
                placeholder="Örn: 101, A-205"
              />
            </div>

            <div className="sm:col-span-3">
              <FormInput
                id={`babySeatCount-${index}`}
                name={`babySeatCount-${index}`}
                label="Bebek Koltuğu Sayısı"
                type="number"
                min="0"
                max="10"
                value={transfer.babySeatCount || 0}
                onChange={(e) => handleTransferChange(index, 'babySeatCount', parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="sm:col-span-3">
              <FormInput
                id={`childSeatCount-${index}`}
                name={`childSeatCount-${index}`}
                label="Çocuk Koltuğu Sayısı"
                type="number"
                min="0"
                max="10"
                value={transfer.childSeatCount || 0}
                onChange={(e) => handleTransferChange(index, 'childSeatCount', parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="sm:col-span-3">
              <FormInput
                id={`collectionAmount-${index}`}
                name={`collectionAmount-${index}`}
                label="Tahsilat Tutarı"
                type="number"
                min="0"
                step="0.01"
                value={transfer.collectionAmount || ''}
                onChange={(e) => handleTransferChange(index, 'collectionAmount', parseFloat(e.target.value) || null)}
                placeholder="0.00"
              />
            </div>

            <div className="sm:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tahsilat Para Birimi
              </label>
              <Select
                value={currencyOptions.find(option => option.value === transfer.collectionCurrency) || null}
                onChange={(selectedOption) => handleSelectChange(index, 'collectionCurrency', selectedOption)}
                options={currencyOptions}
                styles={customStyles}
                placeholder="Para birimi seçin..."
                isClearable
                isSearchable={false}
              />
            </div>

            <div className="sm:col-span-6">
              <FormInput
                id={`driverNote-${index}`}
                name={`driverNote-${index}`}
                label="Şoför Notu"
                type="textarea"
                value={transfer.driverNote || ''}
                onChange={(e) => handleTransferChange(index, 'driverNote', e.target.value)}
                placeholder="Şoför için özel talimatlar..."
                rows={2}
              />
            </div>

            <div className="sm:col-span-6">
              <FormInput
                id={`operationNote-${index}`}
                name={`operationNote-${index}`}
                label="Operasyon Notu"
                type="textarea"
                value={transfer.operationNote || ''}
                onChange={(e) => handleTransferChange(index, 'operationNote', e.target.value)}
                placeholder="Operasyon ekibi için notlar..."
                rows={2}
              />
            </div>
          </div>
        </div>
      ))}

      {onAdd && (
        <div className="text-center">
          <button
            type="button"
            onClick={onAdd}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Transfer Ekle
          </button>
        </div>
      )}
    </div>
  );
};

export default TransferInfo;